'use client';
import React, { useState } from 'react';

import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  alt: string;
  featured?: boolean;
}

const BLOG_POSTS: BlogPost[] = [
{
  id: 'blog-1',
  title: 'How to Prepare for the International Mathematics Olympiad: A Complete Guide',
  excerpt: 'A step-by-step preparation strategy covering number theory, algebra, geometry, and combinatorics — with recommended resources and practice tips from top-ranked students.',
  category: 'Study Tips',
  author: 'OnlineOlympiad Editorial',
  date: 'May 15, 2026',
  readTime: '8 min read',
  image: "https://images.unsplash.com/photo-1596496181871-9681eacf9764",
  alt: 'Student solving mathematics problems on a whiteboard with equations and diagrams',
  featured: true
},
{
  id: 'blog-2',
  title: 'Understanding AI in Education: What Every Student Should Know',
  excerpt: 'Artificial Intelligence is reshaping how we learn. From personalised study plans to AI-powered report cards, here\'s how AI is being used to help students achieve more.',
  category: 'AI & Technology',
  author: 'Dr. Priya Nair',
  date: 'May 10, 2026',
  readTime: '6 min read',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15deb62e2-1772172186408.png",
  alt: 'Futuristic digital brain with glowing neural network connections representing artificial intelligence'
},
{
  id: 'blog-3',
  title: 'Top 5 Science Olympiad Topics Students Struggle With (And How to Master Them)',
  excerpt: 'Data from 50,000+ exam attempts reveals the most challenging topics in the Science Olympiad. Here\'s a targeted guide to conquering each one.',
  category: 'Science',
  author: 'OnlineOlympiad Editorial',
  date: 'May 5, 2026',
  readTime: '7 min read',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad9d6bb2-1766999008911.png",
  alt: 'Science laboratory with test tubes, microscope, and colorful chemical experiments'
},
{
  id: 'blog-4',
  title: 'From Grade 6 to Global Rank #892: Lena\'s Olympiad Journey',
  excerpt: 'A German student shares how she went from a nervous first-timer to a globally ranked olympiad participant — and what she learned along the way.',
  category: 'Student Stories',
  author: 'Lena Müller',
  date: 'April 28, 2026',
  readTime: '5 min read',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1578c9e2e-1767844549353.png",
  alt: 'Happy student holding a certificate and smiling in front of a school building'
},
{
  id: 'blog-5',
  title: 'Why Global Benchmarking Matters More Than School Grades',
  excerpt: 'School grades tell you where you stand in your class. Global benchmarking tells you where you stand in the world. Here\'s why the difference matters for your future.',
  category: 'Education',
  author: 'OnlineOlympiad Editorial',
  date: 'April 20, 2026',
  readTime: '6 min read',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa09aedb-1768387400821.png",
  alt: 'World map with student icons connected by lines representing global education network'
},
{
  id: 'blog-6',
  title: 'Chemistry Olympiad Prep: Mastering Stoichiometry and Reaction Mechanisms',
  excerpt: 'Stoichiometry and organic reaction mechanisms are the two biggest hurdles in the Chemistry Olympiad. This guide breaks them down with worked examples.',
  category: 'Chemistry',
  author: 'Dr. Arun Sharma',
  date: 'April 15, 2026',
  readTime: '9 min read',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_136e1c01f-1764689217558.png",
  alt: 'Chemistry laboratory with colorful chemical reactions in beakers and flasks'
}];


const CATEGORIES = ['All', 'Study Tips', 'AI & Technology', 'Science', 'Chemistry', 'Student Stories', 'Education'];

const categoryColors: Record<string, string> = {
  'Study Tips': 'bg-blue-50 text-blue-700',
  'AI & Technology': 'bg-purple-50 text-purple-700',
  'Science': 'bg-teal-50 text-teal-700',
  'Chemistry': 'bg-green-50 text-green-700',
  'Student Stories': 'bg-amber-50 text-amber-700',
  'Education': 'bg-rose-50 text-rose-700'
};

export default function BlogsPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ?
  BLOG_POSTS :
  BLOG_POSTS.filter((p) => p.category === activeCategory);

  const featured = filtered.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured);

  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      <main className="pt-16">
        {/* Header */}
        <section className="bg-hero-gradient py-14">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 text-center">
            <span className="inline-block px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-semibold text-white mb-4 uppercase tracking-wide">
              OnlineOlympiad Blog
            </span>
            <h1 className="text-4xl font-extrabold text-white mb-3">Insights, Tips & Student Stories</h1>
            <p className="text-slate-300 text-lg max-w-xl mx-auto">
              Expert guides, olympiad preparation strategies, and inspiring stories from students around the world.
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-card border-b border-border py-4 sticky top-16 z-40 shadow-sm">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {CATEGORIES.map((cat) =>
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-150 ${
                activeCategory === cat ?
                'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'}`
                }>
                
                  {cat}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
            {filtered.length === 0 ?
            <div className="text-center py-20 text-muted-foreground">No posts in this category yet.</div> :

            <>
                {/* Featured Post */}
                {featured &&
              <div className="mb-10 bg-card border border-border rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-shadow">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="relative h-64 lg:h-auto overflow-hidden">
                        <img
                      src={featured.image}
                      alt={featured.alt}
                      className="w-full h-full object-cover" />
                    
                        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
                      </div>
                      <div className="p-8 lg:p-10 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="px-2.5 py-1 text-xs font-semibold bg-primary/10 text-primary rounded-full">Featured</span>
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${categoryColors[featured.category] || 'bg-muted text-muted-foreground'}`}>
                            {featured.category}
                          </span>
                        </div>
                        <h2 className="text-2xl font-extrabold text-foreground mb-3 leading-tight">{featured.title}</h2>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-6">{featured.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Calendar size={12} />{featured.date}</span>
                            <span className="flex items-center gap-1"><Clock size={12} />{featured.readTime}</span>
                          </div>
                          <button className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-secondary transition-colors">
                            Read More <ArrowRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
              }

                {/* Post Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((post) =>
                <article key={post.id} className="bg-card border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-200 hover:-translate-y-0.5 flex flex-col">
                      <div className="relative h-48 overflow-hidden">
                        <img
                      src={post.image}
                      alt={post.alt}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                    
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${categoryColors[post.category] || 'bg-muted text-muted-foreground'}`}>
                            <Tag size={10} className="inline mr-1" />{post.category}
                          </span>
                        </div>
                        <h3 className="text-base font-bold text-foreground mb-2 leading-snug line-clamp-2">{post.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1 line-clamp-3">{post.excerpt}</p>
                        <div className="flex items-center justify-between pt-3 border-t border-border">
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Calendar size={11} />{post.date}</span>
                            <span className="flex items-center gap-1"><Clock size={11} />{post.readTime}</span>
                          </div>
                          <button className="text-xs font-semibold text-primary hover:text-secondary transition-colors flex items-center gap-1">
                            Read <ArrowRight size={12} />
                          </button>
                        </div>
                      </div>
                    </article>
                )}
                </div>
              </>
            }
          </div>
        </section>
      </main>
      <Footer />
    </div>);

}